import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LuUsers,
  LuNetwork,
  LuListChecks,
  LuGauge,
  LuPhoneOff,
  LuSparkles,
  LuMail,
} from "react-icons/lu";

import MembersTab from "./MembersTab";
import OrganisationsPanel from "./OrganisationsPanel";
import CustomFieldsPanel from "./CustomFieldsPanel";
import GradingRulesPanel from "./GradingRulesPanel";
import BlacklistPanel from "./BlacklistPanel";
import DailyDigestPanel from "./DailyDigestPanel";
import AiGenerationPanel from "./ai-generation/AiGenerationPanel";

import { getAlgoliaInfos } from "../../redux/actions/algoliaActions";
import getConfig from "../../conf/config";
import myAxios from "../../utils/axiosWrapper";
import { auth } from "../../conf/firebase";

const Settings = () => {
  const { user } = useSelector((state) => state.user);
  const { currentUser } = auth;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { company_config } = useSelector((state) => state.algolia.algolia);
  const isEasierAdmin = user?.is_easier_admin;
  const isSuperAdmin = (user?.permissions || []).includes("SUPER_ADMIN");

  const [organisations, setOrganisations] = useState({});
  const [dirtyTabs, setDirtyTabs] = useState({});
  // Bumped after an AI proposal is applied so the fields / rules panels refetch.
  const [configVersion, setConfigVersion] = useState(0);

  const fetchCompanyOrganisations = async () => {
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/company_organisations`
    );
    setOrganisations(response?.data?.[0] || {});
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    fetchCompanyOrganisations();
  }, [user]);

  useEffect(() => {
    dispatch(getAlgoliaInfos());
  }, [dispatch]);

  const tabs = useMemo(
    () => [
      { key: "members", label: "Members", icon: LuUsers },
      { key: "organisation", label: "Organisation", icon: LuNetwork },
      { key: "custom-analysis", label: "Custom Analysis", icon: LuListChecks },
      { key: "call-grading", label: "Call Grading", icon: LuGauge },
      ...(isEasierAdmin
        ? [{ key: "ai-generation", label: "AI Generation", icon: LuSparkles }]
        : []),
      ...(isSuperAdmin
        ? [
            { key: "blacklist", label: "Blacklisted Numbers", icon: LuPhoneOff },
            { key: "daily-digest", label: "Daily Digest", icon: LuMail },
          ]
        : []),
    ],
    [isSuperAdmin, isEasierAdmin]
  );

  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab : "members";

  // Panels keep their state (drafts, filters, pagination) when the user moves
  // between tabs: each visited panel stays mounted and is only hidden.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));

  const onSelectTab = (tabKey) => {
    setVisitedTabs((previous) => new Set(previous).add(tabKey));
    // Keep the other params (e.g. the AI Generation tab's open `proposal`).
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (tabKey === "members") next.delete("tab");
        else next.set("tab", tabKey);
        return next;
      },
      { replace: true }
    );
  };

  const onDirtyChange = (tabKey) => (isDirty) => {
    setDirtyTabs((previous) =>
      previous[tabKey] === isDirty ? previous : { ...previous, [tabKey]: isDirty }
    );
  };

  const renderTabPanel = (tabKey, panel) => {
    if (!visitedTabs.has(tabKey) && activeTab !== tabKey) {
      return null;
    }
    return <div className={activeTab === tabKey ? "" : "hidden"}>{panel}</div>;
  };

  return (
    <div className="px-6 pb-32">
      <div className="flex items-end justify-between gap-[12px] flex-wrap">
        <div>
          <h1 className="whitespace-nowrap text-[36px] font-semibold text-primary">
            {t("Admin settings")}
          </h1>
          <p className="text-sm text-[#737373] mt-1">
            {t("Manage your team, organisation and call analysis configuration")}
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-1 flex-wrap border-b border-[#E5E5E5]">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onSelectTab(tab.key)}
              className={`relative flex items-center gap-2 px-4 pt-2 pb-3 -mb-px text-sm font-medium transition-colors border-b-2 ${
                isActive
                  ? "text-primary border-primary"
                  : "text-[#737373] border-transparent hover:text-[#404040] hover:border-[#D4D4D4]"
              }`}
            >
              <Icon size={16} />
              {t(tab.label)}
              {dirtyTabs[tab.key] && (
                <span
                  title={t("Unsaved changes")}
                  className="w-2 h-2 rounded-full bg-amber-500"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {renderTabPanel(
          "members",
          <MembersTab
            company_config={company_config}
            organisations={organisations}
            refreshOrganisations={fetchCompanyOrganisations}
          />
        )}
        {renderTabPanel(
          "organisation",
          <OrganisationsPanel
            apiUrl={getConfig().API_URL}
            onOrganisationsChanged={fetchCompanyOrganisations}
          />
        )}
        {renderTabPanel(
          "custom-analysis",
          <CustomFieldsPanel
            apiUrl={getConfig().API_URL}
            company_config={company_config}
            organisations={organisations}
            isReadOnly={!isEasierAdmin}
            onDirtyChange={onDirtyChange("custom-analysis")}
            refreshKey={configVersion}
          />
        )}
        {renderTabPanel(
          "call-grading",
          <GradingRulesPanel
            apiUrl={getConfig().API_URL}
            organisations={organisations}
            isReadOnly={!isEasierAdmin}
            onDirtyChange={onDirtyChange("call-grading")}
            refreshKey={configVersion}
          />
        )}
        {isEasierAdmin &&
          renderTabPanel(
            "ai-generation",
            <AiGenerationPanel
              apiUrl={getConfig().API_URL}
              organisations={organisations}
              // Saving a stale field list soft-deletes what it lacks: never
              // apply while those panels hold unsaved edits.
              blockingReason={
                dirtyTabs["custom-analysis"] || dirtyTabs["call-grading"]
                  ? t(
                      "Enregistrez ou annulez d'abord les modifications en cours des onglets Analyse personnalisée / Notation."
                    )
                  : null
              }
              onConfigApplied={() => setConfigVersion((version) => version + 1)}
            />
          )}
        {isSuperAdmin &&
          renderTabPanel(
            "blacklist",
            <BlacklistPanel
              apiUrl={getConfig().API_URL}
              onDirtyChange={onDirtyChange("blacklist")}
            />
          )}
        {isSuperAdmin &&
          renderTabPanel(
            "daily-digest",
            <DailyDigestPanel
              apiUrl={getConfig().API_URL}
              user={user}
              organisations={organisations}
              company_config={company_config}
              onDirtyChange={onDirtyChange("daily-digest")}
            />
          )}
      </div>
    </div>
  );
};

export default Settings;
